import { LitElement, html, css } from 'lit';

/**
 * Composant <ui-switch>
 * Switch / Toggle réactif et accessible avec transitions fluides
 */
export class UISwitch extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    checked: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    value: { type: String },
    label: { type: String, reflect: true }
  };

  static styles = css`
    :host {
      display: inline-flex;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
      cursor: pointer;
      user-select: none;
    }

    :host([label]) {
      display: block;
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .switch-container {
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    :host([disabled]) {
      cursor: not-allowed;
      opacity: 0.6;
    }

    /* Le conteneur du Switch */
    .switch-track {
      position: relative;
      width: 44px;
      height: 24px;
      background-color: var(--ui-border);
      border-radius: var(--ui-radius-full);
      transition: background-color var(--ui-transition-fast);
      outline: none;
    }

    :host(:hover:not([disabled])) .switch-track {
      background-color: var(--ui-border-hover);
    }

    .switch-track:focus-visible {
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
      border-color: var(--ui-border-focus);
    }

    /* Track quand coché */
    :host([checked]) .switch-track {
      background-color: var(--ui-primary);
    }

    :host([checked]:hover:not([disabled])) .switch-track {
      background-color: var(--ui-primary-hover);
    }

    /* Le Bouton coulissant (Knob) */
    .switch-thumb {
      position: absolute;
      top: 3px;
      left: 3px;
      width: 18px;
      height: 18px;
      background-color: var(--ui-surface);
      border-radius: 50%;
      box-shadow: var(--ui-shadow-sm);
      transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    /* Mouvement du Knob */
    :host([checked]) .switch-thumb {
      transform: translateX(20px);
    }

    .switch-label {
      line-height: 1;
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.checked = false;
    this.disabled = false;
    this.required = false;
    this.value = 'on';
    this.label = '';

    this.addEventListener('click', this._handleClick);
    this.addEventListener('keydown', this._handleKeyDown);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('checked') || changedProperties.has('required') || changedProperties.has('value')) {
      this.internals.setFormValue(this.checked ? this.value || 'on' : null);
      this._updateValidity();
    }
  }

  _updateValidity() {
    if (this.required && !this.checked) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez cocher cette case.', this.shadowRoot.querySelector('.switch-track'));
    } else {
      this.internals.setValidity({});
    }
  }

  _handleClick(e) {
    if (this.disabled) return;
    e.preventDefault();
    this._toggle();
  }

  _handleKeyDown(e) {
    if (this.disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this._toggle();
    }
  }

  _toggle() {
    const nextState = !this.checked;
    this.checked = nextState;
    
    // Déclencher les événements
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('ui-change', {
      detail: { checked: nextState, value: nextState ? this.value : '' },
      bubbles: true,
      composed: true
    }));
  }

  render() {
    return html`
      <!-- Soumission automatique gérée via ElementInternals -->
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="switch-container">
        <div 
          class="switch-track" part="input" 
          tabindex="${this.disabled ? '-1' : '0'}"
          role="switch"
          aria-checked="${this.checked}"
          aria-disabled="${this.disabled}"
        >
          <div class="switch-thumb"></div>
        </div>
        <span class="switch-label">
          <slot></slot>
        </span>
      </div>
    `;
  }
}

if (!customElements.get('ui-switch')) {
  customElements.define('ui-switch', UISwitch);
}
